import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useSnackbar } from "notistack"
import { getStudent, editStudent, getAdmission, removeAdmission } from "../api"
import LoadingContent from "../components/common/LoadingContent"
import TabPanel from "../components/ui/TabPanel"
import EditRoundedIcon from "@mui/icons-material/EditRounded"
import DeleteConfirm from "../components/modals/DeleteConfirm"

const StudentView = () => {
  // Tab state
  const navigate = useNavigate()
  const [value, setValue] = useState(0)
  const handleChange = (_, newValue) => setValue(newValue)
  const { enqueueSnackbar } = useSnackbar()

  // get ID from url
  const { id } = useParams()

  // Personal Details fetch and update
  const [user, setUser] = useState({})
  const [admission, setAdmission] = useState({})
  const [loading, setLoading] = useState(false)
  const [loading2, setLoading2] = useState(false)
  const [userData, setUserData] = useState({})
  const fetchUser = () => {
    setLoading(true)
    getStudent(id)
      .then((res) => {
        setUser(res.data)
        setUserData({
          id,
          motherName: res.data.motherName,
          fatherName: res.data.fatherName,
          address: res.data.address,
        })
      })
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setLoading(false))
  }
  const fetchAdmission = () => {
    setLoading2(true)
    getAdmission(id)
      .then((res) => {
        setAdmission(res.data)
      })
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setLoading2(false))
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setUserData({
      ...userData,
      [name]: value,
    })
  }

  const [updating, setUpdating] = useState(false)
  const updateUser = () => {
    setUpdating(true)
    editStudent(userData)
      .then((res) => {
        enqueueSnackbar("Updated!", { variant: "success" })
        fetchUser()
      })
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setUpdating(false))
  }

  useEffect(() => {
    fetchUser()
    fetchAdmission()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [showConfirmAdmissionDelete, setShowConfirmAdmissionDelete] = useState(false)

  const [removing, setRemoving] = useState(false)

  const deleteAdmission = () => {
    setRemoving(true)
    removeAdmission(id)
      .then(() => navigate(`/admin/students/${id}`))
      .catch((err) => enqueueSnackbar(err.data, { variant: "error" }))
      .finally(() => setRemoving(false))
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 0, sm: 10 },
        pl: 5,
        pt: 3,
      }}
    >
      <LoadingContent loading={loading}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            alignItems: "center",
          }}
        >
          <Avatar sx={{ width: 120, height: 120 }}>
            {user?.user_?.profilePicture}
          </Avatar>
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ mb: "10px" }} variant="h5">
              {user?.user_?.name}
            </Typography>

            <Detail label="Email" value={user?.user_?.email} />
            <Detail label="Phone" value={user?.user_?.phoneNumber} />
          </Box>
        </Box>
      </LoadingContent>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={value} onChange={handleChange}>
            <Tab label="Profile" />
            <Tab label="Admission Details" />
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <Box>
            <Typography
              variant="h6"
              sx={{ letterSpacing: 2 }}
              color="primary"
              mb={2}
            >
              PERSONAL DETAILS
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Field disabled label="Name" value={user?.user_?.name} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field disabled label="Email" value={user?.user_?.email} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Field
                  disabled
                  label="Phone"
                  value={user?.user_?.phoneNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Father's Name"
                  value={userData?.fatherName}
                  name="fatherName"
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Mother's Name"
                  value={userData?.motherName}
                  name="motherName"
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  label="Address"
                  value={userData?.address}
                  name="address"
                  onChange={handleFormChange}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  onClick={updateUser}
                  variant="contained"
                  sx={{ gap: 1 }}
                >
                  {updating ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <EditRoundedIcon fontSize="small" /> Update
                    </>
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
        <TabPanel value={value} index={1}>
          <Box>
            <Typography variant="h6" sx={{ letterSpacing: 2 }} color="primary">
              ADMISSION DETAILS
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }} alignItems="flex-start">
              <Grid container spacing={1} mb={2}>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Institute Id</FormLabel>
                    {admission?.instituteId}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Institute</FormLabel>
                    {admission?.institution}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Department</FormLabel>
                    {admission?.department}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Program</FormLabel>
                    {admission?.program || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Year</FormLabel>
                    {admission?.year || "N/A"}
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Admission Date</FormLabel>
                    {admission?.admissionDate ? admission?.admissionDate.split("T")[0] : "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Enrollment Status</FormLabel>
                    {admission?.enrollmentStatus || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Avail Hostel Facility</FormLabel>
                    {admission?.availHostelFacility || "N/A"}
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    style={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <FormLabel>Avail Transportation Facility</FormLabel>
                    {admission?.availTransportationFacility || "N/A"}
                  </Stack>
                </Grid>
              </Grid>
              <Button
                onClick={() => setShowConfirmAdmissionDelete(true)}
                variant="contained"
                color="error"
              >
                Delete
              </Button>
            </Stack>
          </Box>
        </TabPanel>
      </Box>
      <DeleteConfirm
        type="Admission"
        name={user?.user_?.name}
        isOpen={showConfirmAdmissionDelete}
        close={() => setShowConfirmAdmissionDelete(false)}
        action={deleteAdmission}
        loading={removing}
      />
    </Box>
  )
}

const Field = ({ label, ...props }) => (
  <FormControl fullWidth>
    <FormLabel>{label}</FormLabel>
    <TextField {...props} size="small" />
  </FormControl>
)

const Detail = ({ label, value }) => (
  <Stack direction="row" spacing={2} sx={{ maxWidth: "14rem" }}>
    <Typography variant="body1" color="gray">
      {label}
    </Typography>
    <Typography
      variant="body1"
      style={{
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Stack>
)

export default StudentView
